import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { format, startOfMonth } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Star, AlertTriangle, TrendingUp, TrendingDown, Minus, MessageSquare, ChevronRight } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import OutletFeedbackDrillModal from './OutletFeedbackDrillModal';
import { fmtDateTimeIST } from '../../lib/tz';


const ratingColor = v => v == null ? 'text-[#5C6B64]' : v >= 4 ? 'text-[#2D6A4F]' : v >= 3 ? 'text-[#D4A017]' : 'text-[#C45A41]';

function TrendBadge({ delta }) {
  if (delta == null) return <span className="text-[10px] text-[#5C6B64]">—</span>;
  const Icon = delta > 0.1 ? TrendingUp : delta < -0.1 ? TrendingDown : Minus;
  const cls = delta > 0.1 ? 'text-[#2D6A4F]' : delta < -0.1 ? 'text-[#C45A41]' : 'text-[#5C6B64]';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${cls}`}>
      <Icon className="w-3 h-3" />
      {delta > 0 ? '+' : ''}{delta.toFixed(2)}
    </span>
  );
}

export default function InternalFeedbackTab() {
  const { getHeaders, API } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackList, setFeedbackList] = useState([]);
  const [filterOutlet, setFilterOutlet] = useState('');
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const mtdStartStr = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const [range, setRange] = useState({ key: 'mtd', start_date: mtdStartStr, end_date: todayStr, label: 'MTD' });
  const [drill, setDrill] = useState({ open: false, outletId: null, outletName: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sumParams = new URLSearchParams({
        start_date: range.start_date,
        end_date: range.end_date,
      });
      const listParams = new URLSearchParams({
        // iter180p2 — bump to backend max so 100% of submissions in the
        // selected date range are visible. Previously hardcoded to 50,
        // which silently hid older feedback when an outlet had >50 in MTD.
        limit: '500',
        start_date: range.start_date,
        end_date: range.end_date,
      });
      if (filterOutlet) listParams.set('outlet_id', filterOutlet);
      const [sumRes, listRes] = await Promise.all([
        axios.get(`${API}/admin/feedback/summary?${sumParams.toString()}`, { headers: getHeaders() }),
        axios.get(`${API}/admin/feedback/list?${listParams.toString()}`, { headers: getHeaders() }),
      ]);
      setSummary(sumRes.data);
      setFeedbackList(listRes.data.items || []);
    } catch (e) {
      setSummary({ outlets: [], trend: [] });
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders, filterOutlet, range.start_date, range.end_date]);

  useEffect(() => { load(); }, [load]);

  const aggregates = useMemo(() => {
    const outlets = summary?.outlets || [];
    const negativeCount = outlets.reduce((s, o) => s + ((o.range || o.mtd || {}).negative || 0), 0);
    const totalCount = outlets.reduce((s, o) => s + ((o.range || o.mtd || {}).count || 0), 0);
    return { negativeCount, totalCount };
  }, [summary]);
  const { negativeCount, totalCount } = aggregates;
  const actionRequired = feedbackList.filter(f => (f.overall_rating || 0) <= 2);
  const alertOutlets = (summary?.outlets || []).filter(o => o.alerts && o.alerts.length > 0);

  return (
    <div className="space-y-4">
      {/* Filter strip */}
      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="cx-filter-strip">
        <h3 className="text-sm font-semibold text-[#1A2421]">Internal Feedback Overview</h3>
        <DateRangeFilter value={range} onChange={setRange} testId="admin-feedback-range" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" /></div>
      ) : (<>
      {/* Alerts */}
      {alertOutlets.length > 0 && (
        <Card className="border-[#C45A41]/30 bg-[#FEF2EE] shadow-sm" data-testid="cx-feedback-alerts">
          <CardContent className="py-3 px-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-[#C45A41] shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-[#C45A41] uppercase tracking-wide mb-1">
                  {alertOutlets.length} outlet{alertOutlets.length !== 1 ? 's' : ''} need attention
                </p>
                <ul className="space-y-1">
                  {alertOutlets.flatMap(o =>
                    o.alerts.map((a, i) => (
                      <li key={`${o.outlet_id}-${i}`} className="text-xs text-[#1A2421]">
                        <span className="font-semibold">{o.outlet_name}</span> — {a}
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Outlet comparison grid — cards are now clickable to open drill-down */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="outlet-comparison-grid">
        {(summary?.outlets || []).map(o => {
          const r = o.range || o.mtd || {};
          return (
            <button key={o.outlet_id} type="button"
              onClick={() => setDrill({ open: true, outletId: o.outlet_id, outletName: o.outlet_name })}
              data-testid={`cx-feedback-card-${o.outlet_id}`}
              className={`text-left bg-white rounded-lg shadow-sm border transition-all hover:shadow-md hover:border-[color:var(--color-primary)]/40 hover:-translate-y-px ${o.alerts.length > 0 ? 'border-[#C45A41]/40' : 'border-[#E2E8DF]'}`}>
              <CardHeader className="pb-2 pt-3 px-4">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-sm font-semibold text-[#1A2421] truncate flex items-center gap-1.5">
                    {o.outlet_name}
                    <ChevronRight className="w-3 h-3 text-[#9CA3AF] shrink-0" />
                  </CardTitle>
                  <TrendBadge delta={o.trend_delta} />
                </div>
                <p className="text-[10px] text-[#5C6B64] uppercase tracking-wide">{range.label}</p>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-1">
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="text-center p-2 rounded bg-[#F9F9F8]">
                    <Star className="w-3 h-3 text-amber-500 mx-auto mb-0.5" fill="currentColor" />
                    <p className={`text-lg font-bold tabular-nums ${ratingColor(r.avg)}`}>
                      {r.avg != null ? r.avg.toFixed(2) : '—'}
                    </p>
                    <p className="text-[9px] text-[#5C6B64] uppercase">Avg rating</p>
                  </div>
                  <div className="text-center p-2 rounded bg-[#F9F9F8]">
                    <MessageSquare className="w-3 h-3 text-[color:var(--color-primary)] mx-auto mb-0.5" />
                    <p className="text-lg font-bold tabular-nums text-[#1A2421]">{r.count || 0}</p>
                    <p className="text-[9px] text-[#5C6B64] uppercase">Feedbacks</p>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 text-[10px]">
                  <span className="flex items-center gap-1 text-[#2D6A4F]">
                    <span className="w-2 h-2 rounded-full bg-[#2D6A4F]" /> {r.positive || 0}
                  </span>
                  <span className="flex items-center gap-1 text-[#D4A017]">
                    <span className="w-2 h-2 rounded-full bg-[#D4A017]" /> {r.neutral || 0}
                  </span>
                  <span className="flex items-center gap-1 text-[#C45A41]">
                    <span className="w-2 h-2 rounded-full bg-[#C45A41]" /> {r.negative || 0}
                  </span>
                  <span className="flex items-center gap-1 text-[#5C6B64]">
                    Dine {r.dinein_count || 0} · Del {r.delivery_count || 0}
                  </span>
                  {(r.action_required || 0) > 0 && (
                    <Badge variant="destructive" className="text-[9px]">
                      {r.action_required} ⚠
                    </Badge>
                  )}
                </div>
              </CardContent>
            </button>
          );
        })}
      </div>

      {/* Aggregate strip */}
      <Card className="border-[#E2E8DF] shadow-sm">
        <CardContent className="py-3 px-4 flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-[10px] text-[#5C6B64] uppercase">{range.label} totals</p>
            <p className="text-sm font-bold text-[#1A2421]">{totalCount} feedbacks · {negativeCount} negative</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-[#5C6B64] uppercase">Trend ({range.label})</p>
            <p className="text-sm font-bold text-[#1A2421]">{summary?.trend?.length || 0} days with data</p>
          </div>
        </CardContent>
      </Card>

      {/* Action-required feedback list */}
      {actionRequired.length > 0 && (
        <Card className="border-[#C45A41]/30 shadow-sm" data-testid="action-required-list">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm font-semibold text-[#C45A41]">
              Action required — {actionRequired.length} low-rated feedback
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="space-y-2">
              {actionRequired.slice(0, 10).map(f => (
                <div key={f.id} className="p-3 bg-[#FEF2EE] border border-[#C45A41]/20 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${ratingColor(f.overall_rating)}`}>{f.overall_rating}★</span>
                      <Badge variant="outline" className="text-[9px] uppercase">{f.form_type}</Badge>
                      {f.phone && <span className="text-[10px] text-[#5C6B64]">📞 {f.phone}</span>}
                    </div>
                    <span className="text-[10px] text-[#5C6B64]">{fmtDateTimeIST(f.submitted_at)}</span>
                  </div>
                  {f.comments && <p className="text-xs text-[#1A2421] mt-1">"{f.comments}"</p>}
                  {Array.isArray(f.custom_responses) && f.custom_responses.length > 0 && (
                    <div className="mt-1.5 pl-3 border-l-2 border-[#C45A41]/20 space-y-0.5" data-testid={`admin-custom-${f.id}`}>
                      {f.custom_responses.map((r, i) => (
                        <div key={i} className="text-[10px] leading-tight">
                          <span className="text-[#5C6B64]">{r.question}:</span>{' '}
                          <span className="font-semibold text-[#1A2421]">
                            {r.type === 'rating' ? `${r.answer}★` : String(r.answer)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      </>)}

      <OutletFeedbackDrillModal
        open={drill.open}
        onClose={() => setDrill({ open: false, outletId: null, outletName: '' })}
        outletId={drill.outletId}
        outletName={drill.outletName}
        startDate={range.start_date}
        endDate={range.end_date}
        rangeLabel={range.label}
      />
    </div>
  );
}
